import React from 'react';
import Header from '../components/marketing/Header';
import Seo, { organizationSchema } from '../components/Seo';
import Footer from '../components/marketing/Footer';
import TalkToAlfred from '../components/TalkToAlfred';
import ChatWithAlfred from '../components/ChatWithAlfred';
import {
  DEMO_PHONE_DISPLAY,
  useMarketingPage,
  Hero,
  Section,
  SectionHeading,
  ProblemTable,
  CardGrid,
  DarkStatement,
  Steps,
  MathTable,
  FAQ,
  FinalCTA
} from '../components/marketing/sections';

/**
 * /transportation — the carrier & fleet vertical: outbound calling for driver
 * recruiting.
 *
 * This WAS the homepage (the site was transportation-only from 2026-07-27).
 * On 2026-08-30 the homepage went back to a generic capability pitch that lists
 * the areas we cover, and this page is where the transportation pitch moved to,
 * unchanged. /driver-recruiting now redirects here rather than to /.
 *
 * Source copy: alfred-driver-recruiting one-pager + alfredvoice-site-copy.
 * The single dark moment is "We are not a recruiting agency" — the line the
 * one-pager says does the most work.
 */

const HERO_STATS = [
  { value: '60 sec', label: 'Callback on a new application' },
  { value: '3,000+', label: 'Records per campaign' },
  { value: '24/7', label: 'Nights and weekends' },
  { value: '$995', label: 'Pilot, 500 records' }
];

const FAILURES = [
  {
    n: '01',
    title: 'The applications you never got to.',
    body: 'Every carrier has hundreds or thousands of old applications sitting in the ATS. People who applied, didn’t hear back fast enough, and took a seat somewhere else. That list only grows.'
  },
  {
    n: '02',
    title: 'The applications you got to too late.',
    body: 'Drivers apply at 11pm from a truck stop. Whoever calls first wins — and it is almost never the carrier whose recruiter starts at 8am the next morning. You paid for that application. Someone else hired the driver.'
  }
];

const PAIN_POINTS = [
  { problem: 'Your applicant list is too big for your recruiters to work', solution: 'Calls every record, not the first fifty' },
  { problem: 'Drivers apply at night and on weekends, when nobody’s in the office', solution: 'Runs evenings and weekends' },
  { problem: 'Texts and emails only reach who was going to respond anyway', solution: 'Calls the ones who didn’t reply' },
  { problem: 'Heavy accents and road noise break generic phone systems', solution: 'Built and tested against Houston’s actual driver pool' },
  { problem: 'Nobody has time to call back in the first hour', solution: 'Calls back in under 60 seconds' }
];

const WHAT_ALFRED_DOES = [
  { title: 'Calls back in under 60 seconds', description: '24/7, including nights and weekends.' },
  { title: 'Works your entire dead applicant list', description: 'Thousands of records in days, not months.' },
  { title: 'Screens to your standards', description: 'CDL class, endorsements, verifiable experience, MVR thresholds, job history.' },
  { title: 'Built for your applicant pool', description: 'Punjabi, Eastern European, Somali, Latino — plus road noise and truck-stop reception. This is where generic systems break down, and it’s what we test against. Call the demo line and judge it yourself.' },
  { title: 'Identifies itself as AI', description: 'On every call. Drivers know who they’re talking to.' },
  { title: 'Never gets tired of the list', description: 'Call 3,000 people, twice, at the right hours.' }
];

const ONGOING = [
  { title: 'Reactivation', description: 'We keep working your dormant applicant list until it’s exhausted.' },
  { title: 'Speed-to-lead', description: 'Every new application gets a call in under a minute, day or night. You already pay for those applications. This stops you from wasting them.' }
];

const QUALIFIED = [
  'Meets your CDL class and endorsement requirements',
  'Meets your minimum verifiable experience',
  'Passes your MVR and job-history screens on self-report',
  'Confirms interest in your lanes, pay, and home time',
  'Delivered to your recruiter for live handoff'
];

const COMPLIANCE = [
  { title: 'Alfred says it’s AI', description: 'On every single call. No cloned voices, no pretending.' },
  { title: 'Do-not-call and opt-out', description: 'Honored immediately and permanently.' },
  { title: 'Full recordings and transcripts', description: 'Retained and available to you.' },
  { title: 'Consent and disclosure logging', description: 'On every record.' },
  { title: 'Your applicant data stays yours', description: 'We don’t resell it, and we don’t recruit against you with it.' }
];

const STEPS = [
  { title: 'Discovery call', description: '30 minutes on your list, your criteria, and how your recruiters work today.' },
  { title: 'We build and test it', description: 'Configured on your real screening criteria. You listen until it’s right.' },
  { title: 'Pilot', description: '500 records, one week, full disposition report.' },
  { title: 'Go live', description: 'We work the full list and keep going.' }
];

const FAQ_ITEMS = [
  { q: 'Are you a recruiting agency?', a: 'No. We don’t source, we don’t run your DQ files, and we don’t take a cut of a hire. We make your recruiters faster.' },
  { q: 'Does Alfred tell drivers it’s AI?', a: 'Yes, on every call, without exception. No cloned voices and no pretending to be a person.' },
  { q: 'Will it hold up with heavy accents and road noise?', a: `That’s the decisive question in this vertical, so don’t take our word for it. Call ${DEMO_PHONE_DISPLAY} or have Alfred call you, and judge it against the drivers you actually hire.` },
  { q: 'What do you need from us to start?', a: 'An export of your applicant list (name, phone, apply date, position), your qualification criteria — the actual bar, not the job posting — your recruiters’ availability for live handoffs, and 30 minutes on how your recruiting process runs today. First calls go out within days.' },
  { q: 'What if my recruiter is unavailable at handoff?', a: 'Alfred books a scheduled callback and sends you the full screening summary. That still counts as delivered — we control whether the driver is qualified and on the phone, not your recruiter’s calendar.' },
  { q: 'What if the pilot doesn’t produce?', a: 'You’ve spent $995 and you know. Two qualified drivers on the phone with your recruiter and it has already paid for itself.' },
  { q: 'What happens to my data?', a: 'Your applicant list stays yours and we never sell it. We share it only with the service providers who run Alfred — telephony, cloud, and AI vendors — under confidentiality obligations. Recordings and transcripts are retained and available to you. Full detail is in our privacy policy.' },
  { q: 'Does it work with my number and tools?', a: 'Yes. You forward your existing number — no new hardware — and every call is summarized to your team by email or text.' },
  { q: 'How long until it’s live?', a: 'Days, not months. Short discovery call, we build and test on your real criteria, then go live.' }
];

const TransportationPage = () => {
  useMarketingPage();

  return (
    <div className="min-h-screen bg-paper text-ink">
      <Seo
        title="Outbound Voice AI for Carriers — Every Driver Applicant Called in 60 Seconds | Alfred Voice"
        description="Alfred calls every driver applicant within 60 seconds, around the clock, and works the dead applicant list your recruiters never got back to. Houston-built, tested against a real driver pool."
        path="/transportation"
        structuredData={[organizationSchema()]}
      />
      <Header />

      <Hero
        eyebrow="Outbound Voice AI for Carriers · Houston"
        title="Every driver applicant, called in 60 seconds."
        secondaryHref="#talk-to-alfred"
        secondaryLabel="Have Alfred call me"
        footnote="Not a recruiting agency · Alfred says it’s AI on every call · first calls go out within days"
        stats={HERO_STATS}
      >
        We call every applicant in 60 seconds, around the clock, and work the dead list you never
        got back to. Your recruiters only talk to qualified drivers.
      </Hero>

      {/* The two failures */}
      <Section tone="paper">
        <div className="max-w-4xl mx-auto px-6">
          <SectionHeading
            eyebrow="The two failures"
            title="You're not short on applicants. You're short on hours."
            subtitle="Two things are costing you seats right now."
          />
          <div className="grid md:grid-cols-2 gap-6">
            {FAILURES.map(({ n, title, body }) => (
              <div key={n} className="fade-in bg-warm border border-hair rounded-brand p-8">
                <div className="text-3xl font-bold text-brass mb-4">{n}</div>
                <h3 className="text-xl font-bold mb-3">{title}</h3>
                <p className="text-slateink font-light leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
          <p className="mt-10 text-lg text-slateink font-light leading-relaxed max-w-3xl mx-auto text-center">
            Meanwhile an empty truck costs you revenue every day it sits, and a recruiting agency
            charges <strong className="text-ink font-semibold">$500 to $5,000 per seated driver</strong> to
            solve a problem that starts with a phone call nobody had time to make.
          </p>
        </div>
      </Section>

      {/* Hear Alfred — the strongest asset, kept high on the page */}
      <Section id="talk-to-alfred" tone="warm">
        <div className="max-w-6xl mx-auto px-6">
          <SectionHeading
            eyebrow="Hear it for yourself"
            title="Don't take our word for it."
            subtitle={`Pick the call, drop your number, and Alfred rings you in about a minute from ${DEMO_PHONE_DISPLAY}.`}
          />
          <TalkToAlfred showHeading={false} />
        </div>
      </Section>

      {/* What Alfred does */}
      <Section tone="paper">
        <div className="max-w-6xl mx-auto px-6">
          <SectionHeading
            eyebrow="What Alfred does"
            title="Screens the applicant. Hands your recruiter a live conversation."
            subtitle="Only with someone worth talking to."
          />
          <CardGrid items={WHAT_ALFRED_DOES} tone="paper" />
        </div>
      </Section>

      <ProblemTable title="Sound familiar?" rows={PAIN_POINTS} tone="warm" />

      {/* The one dark moment */}
      <DarkStatement
        eyebrow="What we are not"
        title="We are not a recruiting agency."
        closer="We make your recruiters faster."
        tone="paper"
      >
        <p>We don&rsquo;t source, we don&rsquo;t run your DQ files, and we don&rsquo;t take a cut of a hire.</p>
      </DarkStatement>

      {/* Why outbound is different */}
      <Section tone="warm">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <span className="av-eyebrow mb-3">Why outbound is different</span>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-8">
            Most AI answers the phone. Alfred picks it up.
          </h2>
          <div className="space-y-6 text-lg text-slateink font-light leading-relaxed">
            <p>
              Answering services and AI receptionists wait for someone to call you. That&rsquo;s the easy half.
            </p>
            <p>
              The hard half is the list — the applicants who went cold, the drivers who applied at
              midnight and never heard back. That work is economically impossible for a human team,
              which is why it never gets done.
            </p>
            <p className="text-2xl font-bold text-ink">That&rsquo;s the only thing we do.</p>
          </div>
        </div>
      </Section>

      {/* The pilot */}
      <Section id="pricing" tone="paper">
        <div className="max-w-4xl mx-auto px-6">
          <SectionHeading
            eyebrow="Small enough to approve today"
            title="The pilot — $995"
            subtitle="500 records from your dead applicant list. One week."
          />
          <p className="text-slateink font-light mb-4">
            Full disposition report on every record: qualified and interviewed, callback requested,
            not qualified with reason, not interested, bad number.
          </p>
          <p className="text-slateink font-light mb-12">
            Fast enough to judge on results, not promises.
          </p>

          <h3 className="text-2xl font-bold tracking-tight mb-6">
            What the pilot has to produce to pay for itself
          </h3>
          <MathTable
            tone="paper"
            rows={[
              ['Pilot cost', '$995'],
              ['Value of one qualified interview', '~$650, if 1 in 3 seats against a $2,000 agency fee'],
              ['Interviews needed to break even', '1.5'],
              ['Rate required on 500 records', '0.3%']
            ]}
          />
          <p className="mt-6 text-lg">
            Two qualified drivers on the phone with your recruiter and the pilot has paid for itself.
          </p>
        </div>
      </Section>

      {/* Ongoing */}
      <Section tone="warm">
        <div className="max-w-4xl mx-auto px-6">
          <SectionHeading
            eyebrow="Month-to-month"
            title="Ongoing — $1,250/month"
            subtitle="Includes your first 5 qualified interviews. $175 per qualified interview after that."
          />
          <CardGrid items={ONGOING} cols={2} tone="warm" />
          <p className="mt-8 text-center text-slateink font-light">
            Both campaigns run continuously. No long-term contract.
          </p>
        </div>
      </Section>

      {/* What "qualified interview" means */}
      <Section tone="paper">
        <div className="max-w-4xl mx-auto px-6">
          <SectionHeading
            eyebrow="Defined in writing before the first invoice"
            title={`What "qualified interview" means`}
            subtitle="Typically:"
          />
          <ul className="space-y-4 max-w-2xl mx-auto mb-12">
            {QUALIFIED.map((item) => (
              <li key={item} className="fade-in flex items-start gap-4 border-b border-hair pb-4">
                <span className="flex-shrink-0 mt-2 w-1.5 h-1.5 rounded-full bg-brass" />
                <span className="text-slateink font-light">{item}</span>
              </li>
            ))}
          </ul>

          <div className="max-w-3xl mx-auto space-y-6 text-slateink font-light leading-relaxed">
            <p className="text-lg font-semibold text-ink">
              You are not billed for a booked slot. You are billed when a qualified driver is
              delivered to your recruiter.
            </p>
            <p>
              <strong className="text-ink font-semibold">Handoff SLA.</strong> We hold the driver on
              the line and transfer. If your recruiter is unavailable, Alfred books a scheduled
              callback and sends you the full screening summary — that still counts as delivered. We
              control whether the driver is qualified and on the phone; we don&rsquo;t control your
              recruiter&rsquo;s calendar, and neither of us should be guessing about which of those
              we&rsquo;re billing for.
            </p>
            <p>
              We do not bill per seated driver, because we don&rsquo;t control your DQ process, your drug
              screen, your orientation, or a competing offer. You shouldn&rsquo;t pay us for those, and we
              shouldn&rsquo;t profit from them.
            </p>
            <p>
              <strong className="text-ink font-semibold">What it costs you either way:</strong> at
              $175 per qualified interview, even three interviews to one seated driver puts you around
              $525 per hire against a $500&ndash;$5,000 agency fee — and you keep the relationship, the
              applicant data, and the process. Break-even on the monthly plan is under 2 interviews.
            </p>
          </div>
        </div>
      </Section>

      {/* Compliance */}
      <Section tone="warm">
        <div className="max-w-6xl mx-auto px-6">
          <SectionHeading
            eyebrow="Compliance"
            title="We built the boring part."
            subtitle="Cold outbound has rules, and they're getting stricter. We'd rather be the vendor that already follows them."
          />
          <CardGrid items={COMPLIANCE} tone="warm" />
        </div>
      </Section>

      <Steps steps={STEPS} tone="paper" />

      <FAQ items={FAQ_ITEMS} tone="warm" />

      <FinalCTA
        heading="The driver who applied last night is on the phone with someone."
        body="Send us 500 records off your dead list. One week, and you'll know."
      />

      <Footer />

      <ChatWithAlfred source="landing-transportation" callNumber={DEMO_PHONE_DISPLAY} />
    </div>
  );
};

export default TransportationPage;
